/**
 * utils/modelFailure.js
 * Recognises the errors that mean "no model could answer because of the user's keys or accounts" and turns the
 * server's raw attempt list ("All models failed. Attempts: [{model,status,error|reason}, ...]") into a short,
 * human summary. Returns null for any other error, so those keep their normal handling.
 */

const KEY_PROBLEM = /invalid api key|incorrect api key|api key not (configured|valid)|no api key|unauthori[sz]ed|authentication|\b401\b|\b403\b/i;
const CREDIT_PROBLEM = /insufficient|no credits|credits? remaining|out of credits|balance|billing|quota|suspended|payment|can only afford|\b402\b/i;
const UNAVAILABLE = /\b404\b|no longer available|not found|does not exist|do not have access|unavailable|deprecated|retired/i;
const RATE = /rate.?limit|too many requests|\b429\b/i;

const GROUPS = [
  { id: 'credits', label: 'Out of credits or over quota', test: (e) => CREDIT_PROBLEM.test(e) },
  { id: 'key', label: 'API key missing or not accepted', test: (e) => KEY_PROBLEM.test(e) },
  { id: 'unavailable', label: 'Model no longer available', test: (e) => UNAVAILABLE.test(e) },
  { id: 'rate', label: 'Temporarily rate limited', test: (e) => RATE.test(e) },
];

function parseAttempts(message) {
  const start = message.indexOf('[');
  const end = message.lastIndexOf(']');
  if (start < 0 || end <= start) return [];
  try {
    const list = JSON.parse(message.slice(start, end + 1));
    return Array.isArray(list) ? list.filter((a) => a && typeof a === 'object' && a.model) : [];
  } catch {
    return [];
  }
}

export function analyzeModelFailure(raw) {
  const message = typeof raw === 'string' ? raw : '';
  const allFailed = /all (?:ai )?models failed/i.test(message);
  const singleKeyIssue = !allFailed && (KEY_PROBLEM.test(message) || CREDIT_PROBLEM.test(message)) && /api key|credits?|billing|quota|balance/i.test(message);
  if (!allFailed && !singleKeyIssue) return null;

  const attempts = parseAttempts(message);
  const groups = [];
  const counted = new Set();
  for (const g of GROUPS) {
    const models = attempts
      .filter((a) => a.status === 'failed' && !counted.has(a.model) && g.test(String(a.error || '')))
      .map((a) => { counted.add(a.model); return a.model; });
    if (models.length) groups.push({ id: g.id, label: g.label, models });
  }
  const other = attempts.filter((a) => a.status === 'failed' && !counted.has(a.model)).map((a) => a.model);
  if (other.length) groups.push({ id: 'other', label: 'Returned an error', models: other });
  const skipped = attempts.filter((a) => a.status === 'skipped').map((a) => a.model);

  return {
    tried: attempts.filter((a) => a.status === 'failed').length,
    groups,
    skipped,
    chatText: '⚠️ None of your models could answer this. It is usually an API key or credit issue, not your question. Open **Fix model keys** below to resolve it.',
  };
}

// Provider errors can echo the key that was rejected; never show one. Mirrors server/src/utils/redactSecrets.js.
const SECRET_PATTERNS = [
  /((?:api[_ -]?key|token|secret)\W{1,4}(?:provided|is|=|:)?\W{0,3})(?=[A-Za-z0-9._*-]*[\d*])[A-Za-z0-9._*-]{12,}/gi,   // keyed phrases first, so a bare key inside one isn't redacted twice
  /\bsk-[A-Za-z0-9_-]{8,}\b/g,
  /\bAIza[0-9A-Za-z_-]{20,}\b/g,
  /\bgsk_[A-Za-z0-9]{16,}\b/g,
  /\bxai-[A-Za-z0-9]{16,}\b/g,
  /\b(?:ghp|gho|github_pat|glpat)[_-][A-Za-z0-9_-]{16,}\b/g,
  /(Bearer\s+)[A-Za-z0-9._~+/=-]{12,}/gi,
  /([?&](?:key|api_key|apikey|token)=)[^&\s"']+/gi,
];

export function redactSecrets(text) {
  let out = String(text ?? '');
  for (const re of SECRET_PATTERNS) out = out.replace(re, (m, prefix) => (typeof prefix === 'string' && prefix ? `${prefix}[redacted]` : '[redacted]'));
  return out;
}

export const GENERIC_MODEL_FAILURE = "None of your AI models could answer this. Check your API keys and credits in Models, then try again.";

/** Text safe to show for a failed run: the generic message when no model could answer, otherwise the error with secrets removed. */
export function friendlyRunError(text) {
  const raw = String(text ?? '');
  return analyzeModelFailure(raw) ? GENERIC_MODEL_FAILURE : redactSecrets(raw);
}
